import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';
import Select from './Select';

const QuickAddModal = ({ isOpen, onClose, defaultModule }) => {
  const [activeTab, setActiveTab] = useState('property');
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (isOpen && defaultModule) {
      if (defaultModule?.includes('properties')) setActiveTab('property');
      else if (defaultModule?.includes('certificate')) setActiveTab('certificate');
      else if (defaultModule?.includes('maintenance')) setActiveTab('maintenance');
    }
  }, [isOpen, defaultModule]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e?.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e?.preventDefault();
    console.log('Form submitted:', { type: activeTab, data: formData });
    onClose();
    setFormData({});
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const propertyTypes = [
    { value: 'house', label: 'House' },
    { value: 'flat', label: 'Flat' },
    { value: 'apartment', label: 'Apartment' },
    { value: 'studio', label: 'Studio' },
  ];

  const certificateTypes = [
    { value: 'gas', label: 'Gas Safety Certificate' },
    { value: 'epc', label: 'Energy Performance Certificate' },
    { value: 'eicr', label: 'Electrical Installation Certificate' },
    { value: 'fire', label: 'Fire Safety Certificate' },
  ];

  const maintenancePriorities = [
    { value: 'urgent', label: 'Urgent' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' },
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e?.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-semibold text-foreground font-heading">Quick Add</h2>
          <button
            onClick={onClose}
            className="h-10 w-10 rounded-lg bg-input border border-border flex items-center justify-center transition-smooth hover:bg-muted"
            aria-label="Close"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="flex gap-2 mb-6 border-b border-border">
          <button
            onClick={() => setActiveTab('property')}
            className={`px-4 py-2 text-sm font-medium transition-smooth font-caption ${
              activeTab === 'property' ?'text-accent border-b-2 border-accent' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Property
          </button>
          <button
            onClick={() => setActiveTab('certificate')}
            className={`px-4 py-2 text-sm font-medium transition-smooth font-caption ${
              activeTab === 'certificate'
                ? 'text-accent border-b-2 border-accent' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Certificate
          </button>
          <button
            onClick={() => setActiveTab('maintenance')}
            className={`px-4 py-2 text-sm font-medium transition-smooth font-caption ${
              activeTab === 'maintenance' ?'text-accent border-b-2 border-accent' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Maintenance
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {activeTab === 'property' && (
            <div className="space-y-4">
              <Input
                label="Property Address"
                type="text"
                placeholder="Enter full address"
                required
                value={formData?.address || ''}
                onChange={(e) => handleChange('address', e?.target?.value)}
              />
              <Select
                label="Property Type"
                options={propertyTypes}
                placeholder="Select type"
                required
                value={formData?.propertyType || ''}
                onChange={(value) => handleChange('propertyType', value)}
              />
              <Input
                label="Number of Bedrooms"
                type="number"
                placeholder="Enter number"
                min="0"
                required
                value={formData?.bedrooms || ''}
                onChange={(e) => handleChange('bedrooms', e?.target?.value)}
              />
              <Input
                label="Monthly Rent (£)"
                type="number"
                placeholder="Enter amount"
                min="0"
                required
                value={formData?.rent || ''}
                onChange={(e) => handleChange('rent', e?.target?.value)}
              />
            </div>
          )}

          {activeTab === 'certificate' && (
            <div className="space-y-4">
              <Select
                label="Certificate Type"
                options={certificateTypes}
                placeholder="Select certificate type"
                required
                value={formData?.certificateType || ''}
                onChange={(value) => handleChange('certificateType', value)}
              />
              <Input
                label="Property Reference"
                type="text"
                placeholder="Enter property reference"
                required
                value={formData?.propertyRef || ''}
                onChange={(e) => handleChange('propertyRef', e?.target?.value)}
              />
              <Input
                label="Issue Date"
                type="date"
                required
                value={formData?.issueDate || ''}
                onChange={(e) => handleChange('issueDate', e?.target?.value)}
              />
              <Input
                label="Expiry Date"
                type="date"
                required
                value={formData?.expiryDate || ''}
                onChange={(e) => handleChange('expiryDate', e?.target?.value)}
              />
              <Input
                label="Issued By"
                type="text"
                placeholder="Enter contractor name"
                required
                value={formData?.issuedBy || ''}
                onChange={(e) => handleChange('issuedBy', e?.target?.value)}
              />
            </div>
          )}

          {activeTab === 'maintenance' && (
            <div className="space-y-4">
              <Input
                label="Job Title"
                type="text"
                placeholder="Enter job description"
                required
                value={formData?.jobTitle || ''}
                onChange={(e) => handleChange('jobTitle', e?.target?.value)}
              />
              <Input
                label="Property Reference"
                type="text"
                placeholder="Enter property reference"
                required
                value={formData?.propertyRef || ''}
                onChange={(e) => handleChange('propertyRef', e?.target?.value)}
              />
              <Select
                label="Priority"
                options={maintenancePriorities}
                placeholder="Select priority"
                required
                value={formData?.priority || ''}
                onChange={(value) => handleChange('priority', value)}
              />
              <Input
                label="Description"
                type="text"
                placeholder="Enter detailed description"
                required
                value={formData?.description || ''}
                onChange={(e) => handleChange('description', e?.target?.value)}
              />
              <Input
                label="Reported By"
                type="text"
                placeholder="Enter tenant/agent name"
                required
                value={formData?.reportedBy || ''}
                onChange={(e) => handleChange('reportedBy', e?.target?.value)}
              />
            </div>
          )}

          <div className="flex gap-3 mt-6 pt-6 border-t border-border">
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              fullWidth
              iconName="Check"
              iconPosition="left"
            >
              Add {activeTab === 'property' ? 'Property' : activeTab === 'certificate' ? 'Certificate' : 'Job'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuickAddModal;